import { getApiUrl, getAuthHeaders, API_CONFIG } from '../config/api';
import { Doctor } from '../types';

export interface CreateDoctorData {
  email: string;
  password?: string;
  phoneNumber: string;
  fullName: string;
  specialization?: string;
  isActive?: boolean;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  departmentId?: number;
  experience?: string;
  qualifications?: string;
  licenseNumber?: string;
  aboutYourself?: string;
  profileImage?: File;
}

export interface UpdateDoctorData {
  email?: string;
  phoneNumber?: string;
  fullName?: string;
  specialization?: string;
  isActive?: boolean;
  password?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  departmentId?: number;
  experience?: string;
  qualifications?: string;
  licenseNumber?: string;
  aboutYourself?: string;
  profileImage?: File;
}

export interface GetDoctorsParams {
  page?: number;
  limit?: number;
  isActive?: boolean;
  isApproved?: boolean;
  search?: string;
  departmentId?: number;
}

export interface DoctorsResponse {
  success: boolean;
  data: {
    doctors: Doctor[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface DoctorResponse {
  success: boolean;
  data: Doctor;
  message?: string;
}

class DoctorService {
  async getDoctors(params: GetDoctorsParams = {}): Promise<DoctorsResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.isApproved !== undefined) queryParams.append('isApproved', params.isApproved.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.departmentId) queryParams.append('departmentId', params.departmentId.toString());

    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_DOCTORS}?${queryParams}`), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch doctors');
    }

    return response.json();
  }

  async getDoctor(id: number): Promise<DoctorResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_DOCTOR_BY_ID(id)), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch doctor');
    }

    return response.json();
  }

  async createDoctor(data: CreateDoctorData): Promise<DoctorResponse> {
    const formData = new FormData();
    
    // Add all fields to FormData
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'profileImage' && value instanceof File) {
          formData.append('profileImage', value);
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    const token = localStorage.getItem('admin_token');
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_DOCTORS), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create doctor');
    }

    return response.json();
  }

  async updateDoctor(id: number, data: UpdateDoctorData): Promise<DoctorResponse> {
    const formData = new FormData();
    
    // Add all fields to FormData
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'profileImage' && value instanceof File) {
          formData.append('profileImage', value);
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    const token = localStorage.getItem('admin_token');
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_DOCTOR_BY_ID(id)), {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update doctor');
    }

    return response.json();
  }

  async deleteDoctor(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_DOCTOR_BY_ID(id)), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete doctor');
    }

    return response.json();
  }

  async updateDoctorStatus(id: number, isActive?: boolean, isApproved?: boolean): Promise<DoctorResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_DOCTOR_STATUS(id)), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ isActive, isApproved }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update doctor status');
    }

    return response.json();
  }

  async bulkUpdateDoctors(doctorIds: number[], updates: UpdateDoctorData): Promise<{ success: boolean; message: string }> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_DOCTORS_BULK), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ doctorIds, updates }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to bulk update doctors');
    }

    return response.json();
  }
}

export const doctorService = new DoctorService();
